import React, { useState, useRef, useEffect, useMemo } from 'react';
import { Link, useParams } from 'react-router-dom';
import { FaFacebookF, FaInstagram, FaLinkedinIn, FaTwitter, FaWhatsapp, FaTelegram } from 'react-icons/fa';
import { FiChevronLeft, FiChevronRight } from 'react-icons/fi';
import '../assets/css/ProductDetail.css';
import '../assets/css/ProductFinishes.css';
import '../assets/css/ProductTabs.css';
import '../assets/css/RelatedProducts.css';
import EnquiryForm from '../components/EnquiryForm';
import { API_BASE_URL, UPLOADS_BASE_URL } from '../config';

// Import product images
import mainImg1 from '../assets/images/inner/1.JPG';
import mainImg2 from '../assets/images/inner/2.JPG';
import mainImg3 from '../assets/images/inner/3.JPG';
import mainImg4 from '../assets/images/inner/4.jpg';

// Import wooden finish images
import wood1 from '../assets/images/wooden/1.png';
import wood2 from '../assets/images/wooden/2.png';
import wood3 from '../assets/images/wooden/3.png';
import wood4 from '../assets/images/wooden/4.png';
import wood5 from '../assets/images/wooden/5.png';
import wood6 from '../assets/images/wooden/6.png';
import wood7 from '../assets/images/wooden/7.png';
import wood8 from '../assets/images/wooden/8.png';
import wood9 from '../assets/images/wooden/9.png';

// Import related product images
import relatedProduct1 from '../assets/images/products/1.JPG';
import relatedProduct2 from '../assets/images/products/2.JPG';
import relatedProduct3 from '../assets/images/products/3.JPG';
import relatedProduct4 from '../assets/images/products/4.jpg';
import relatedProduct5 from '../assets/images/products/5.png';

const ProductDetailPage = () => {
  const { id } = useParams();
  const [product, setProduct] = useState(null);
  const [relatedProducts, setRelatedProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [mainImage, setMainImage] = useState('');
  const [activeIndex, setActiveIndex] = useState(0);
  const [activeTab, setActiveTab] = useState('wooden');
  const [activeDetailTab, setActiveDetailTab] = useState('description');
  const [isEnquiryOpen, setIsEnquiryOpen] = useState(false);
  
  // Product images
  const images = [mainImg1, mainImg2, mainImg3, mainImg4];
  
  // Product finishes data
  const finishesData = {
    sizes: [
      { id: 1, image: wood1, name: 'Standard (160x80cm)' },
      { id: 2, image: wood2, name: 'Large (180x90cm)' },
      { id: 3, image: wood3, name: 'Executive (200x100cm)' },
      { id: 4, image: wood4, name: 'Custom Size' }
    ],
    materials: [
      { id: 1, image: wood5, name: 'Egger German Board' },
      { id: 2, image: wood6, name: 'MDF Board' },
      { id: 3, image: wood7, name: 'Particle Board' },
      { id: 4, image: wood8, name: 'Solid Wood' }
    ],
    wooden: [
      { id: 1, image: wood1, name: 'Natural Davos Oak' },
      { id: 2, image: wood2, name: 'Shorewood' },
      { id: 3, image: wood3, name: 'Pacific Walnut' },
      { id: 4, image: wood4, name: 'Truffle Brown Davos Oak' },
      { id: 5, image: wood5, name: 'Natural Dijon Walnut' },
      { id: 6, image: wood6, name: 'Lincoln Walnut' },
      { id: 7, image: wood7, name: 'Grey Brown Whitriver Oak' },
      { id: 8, image: wood8, name: 'Dark Hunton Oak' },
      { id: 9, image: wood9, name: 'Cognac Kendal Oak' }
    ],
    solid: [
      { id: 1, image: wood1, name: 'Pure White' },
      { id: 2, image: wood2, name: 'Cashmere' },
      { id: 3, image: wood3, name: 'Light Grey' },
      { id: 4, image: wood4, name: 'Dust Grey' },
      { id: 5, image: wood5, name: 'Stone Grey' },
      { id: 6, image: wood6, name: 'Black' }
    ],
    marble: [
      { id: 1, image: wood7, name: 'Carrara Marble' },
      { id: 2, image: wood8, name: 'Black Marble' },
      { id: 3, image: wood9, name: 'Emperador' },
      { id: 4, image: wood1, name: 'Calacatta Gold' }
    ]
  };
  
  // Slider state
  const sliderRef = useRef(null);
  const [currentSlide, setCurrentSlide] = useState(0);

  // Fetch product details
  useEffect(() => {
    const fetchProductDetails = async () => {
      try {
        setLoading(true);
        const response = await fetch(`${API_BASE_URL}products.php?id=${id}`);
        if (!response.ok) {
          throw new Error('Product not found');
        }
        const data = await response.json();
        
        // Set the product data directly since the API now returns properly structured data
        setProduct(data);
        
        console.log('📦 Product Data Received:', {
          id: data.id,
          name: data.name,
          length: data.length,
          width: data.width,
          height: data.height,
          hasDimensions: !!(data.length || data.width || data.height)
        });
        
        if (data.images && data.images.length > 0) {
          setMainImage(`${UPLOADS_BASE_URL}products/${data.images[0]}`);
        }

        // Set the first available finish tab as active
        if (data?.materials?.length > 0) {
          setActiveTab('materials');
        } else if (data?.wooden?.length > 0) {
          setActiveTab('wooden');
        } else if (data?.colors?.length > 0) {
          setActiveTab('colors');
        } else if (data?.marbles?.length > 0) {
          setActiveTab('marble');
        } else if (data?.length || data?.width || data?.height || (data?.size_variants && data.size_variants.length > 0)) {
          setActiveTab('size');
        }

        // Debug logging for finishes
        console.log('🔍 Product Finishes Available:');
        console.log(`  - Materials: ${data?.materials?.length || 0}`);
        console.log(`  - Wooden: ${data?.wooden?.length || 0}`);
        console.log(`  - Colors: ${data?.colors?.length || 0}`);
        console.log(`  - Marbles: ${data?.marbles?.length || 0}`);
        console.log(`  - Dimensions: Length(${data?.length || 'N/A'}), Width(${data?.width || 'N/A'}), Height(${data?.height || 'N/A'})`);
        console.log(`  - Size Variants: ${data?.size_variants ? data.size_variants.length : 0}`);
        console.log(`  - Has any finishes: ${
          (data?.materials?.length > 0) ||
          (data?.wooden?.length > 0) ||
          (data?.colors?.length > 0) ||
          (data?.marbles?.length > 0) ||
          (data?.length || data?.width || data?.height) ||
          (data?.size_variants && data.size_variants.length > 0)
        }`);

        // Fetch related products
        const relatedResponse = await fetch(`${API_BASE_URL}products.php?subcategory=${data.subcategory_name}`);
        const relatedData = await relatedResponse.json();
        // Filter out current product and limit to 3 items
        const filteredRelated = relatedData
          .filter(item => item.id !== data.id)
          .slice(0, 10);
        setRelatedProducts(filteredRelated);
      } catch (error) {
        console.error('Error fetching product:', error);
        setError(error.message);
      } finally {
        setLoading(false);
      }
    };

    if (id) {
      fetchProductDetails();
    }
  }, [id]);

  // Handle thumbnail click
  const handleThumbnailClick = (img, index) => {
    setMainImage(`${UPLOADS_BASE_URL}products/${img}`);
    setActiveIndex(index);
  };

  // Handle tab click
  const handleTabClick = (tabName) => {
    setActiveTab(tabName);
  };
  
  // Handle detail tab click
  const handleDetailTabClick = (tabName) => {
    setActiveDetailTab(tabName);
  };

  // Handle slider navigation
  const handleSlideChange = (direction) => {
    const slider = sliderRef.current;
    if (!slider) return;
    
    const windowWidth = window.innerWidth;
    let visibleItems = 3;
    
    if (windowWidth <= 576) {
      visibleItems = 1;
    } else if (windowWidth <= 768) {
      visibleItems = 2;
    }
    
    const itemWidth = slider.firstChild.offsetWidth + 20;
    const maxSlide = relatedProducts.length - visibleItems;
    
    let newSlide;
    if (direction === 'next') {
      newSlide = currentSlide + 1 > maxSlide ? maxSlide : currentSlide + 1;
    } else {
      newSlide = currentSlide - 1 < 0 ? 0 : currentSlide - 1;
    }
    
    setCurrentSlide(newSlide);
    slider.style.transform = `translateX(-${newSlide * itemWidth}px)`;
  };

  // Handle window resize
  useEffect(() => {
    const handleResize = () => {
      if (sliderRef.current) {
        setCurrentSlide(0);
        sliderRef.current.style.transform = 'translateX(0)';
      }
    };
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const handleEnquiryClick = () => {
    setIsEnquiryOpen(true);
  };

  const handleEnquiryClose = () => {
    setIsEnquiryOpen(false);
  };

  const handleWhatsAppClick = () => {
    const phoneNumber = '971501900982';
    const productInfo = product.subcategory_name 
      ? `${product.name} (${product.category_name} > ${product.subcategory_name})`
      : `${product.name} (${product.category_name})`;
    
    const message = encodeURIComponent(`Hello! I am interested in the "${productInfo}". Can you please provide more information about this product including pricing and availability?`);
    const whatsappUrl = `https://api.whatsapp.com/send?phone=${phoneNumber}&text=${message}`;
    window.open(whatsappUrl, '_blank');
  };

  // Helper function to check if any finishes are available
  const hasFinishes = () => {
    return (
      (product?.materials?.length > 0) ||
      (product?.wooden?.length > 0) ||
      (product?.colors?.length > 0) ||
      (product?.marbles?.length > 0) ||
      (product?.length || product?.width || product?.height) ||
      (product?.size_variants && product.size_variants.length > 0)
    );
  };

  if (loading) {
    return (
      <main className="main-content">
        <div className="loading-container">
        </div>
      </main>
    );
  }

  if (error || !product) {
    return (
      <div className="product-detail-page">
        <div className="error-message">
          {error || 'Product not found'}
          <Link to="/" className="back-link">Return to Home</Link>
        </div>
      </div>
    );
  }

  const shareUrl = window.location.href;
  const shareTitle = encodeURIComponent(product.name);

  return (
    <div className="product-detail-page">
     
      
      <div className="product-detail-container">
        <div className="product-detail">
          {/* Product Images */}
          <div className="product-images">
            <div className="main-image">
              <img src={mainImage} alt={product.name} />
            </div>
            <div className="thumbnail-container">
              {product.images.map((img, index) => (
                <div 
                  key={index} 
                  className={`thumbnail ${index === activeIndex ? 'active' : ''}`}
                  onClick={() => handleThumbnailClick(img, index)}
                >
                  <img 
                    src={`${UPLOADS_BASE_URL}products/${img}`} 
                    alt={`${product.name} - View ${index + 1}`} 
                  />
                </div>
              ))}
            </div>
          </div>
          
          {/* Product Info */}
          <div className="product-info">
            <h1 className="product-title">{product.name}</h1>
            
            <div className="product-category-info">
              <span className="product-category">
                Category: <Link to={`/collection/${product.category_name.toLowerCase()}`}>
                  <strong>{product.category_name}</strong>
                </Link>
              </span>
              
              {product.subcategory_name && (
                <span className="subcategory">
                  <i className="fas fa-arrow-right subcategory-icon"></i>
                  Subcategory: <strong>{product.subcategory_name}</strong>
                </span>
              )}
           
            </div>
            <br></br>
            <div className="product-description mt-2">
              {(() => {
                const extractStepsFromDescription = (htmlString) => {
                  if (!htmlString || typeof htmlString !== 'string') return [];

                  // Prefer explicit list items if present
                  try {
                    const container = document.createElement('div');
                    container.innerHTML = htmlString;
                    const liNodes = container.querySelectorAll('li');
                    if (liNodes.length > 0) {
                      return Array.from(liNodes)
                        .map((li) => (li.textContent || '')
                          .replace(/\s+/g, ' ')
                          .trim())
                        .filter(Boolean);
                    }
                  } catch (_) {
                    // Ignore DOM parsing errors and fallback to text processing
                  }

                  // Fallback: convert common HTML breaks to newlines
                  let text = htmlString
                    .replace(/<\s*br\s*\/?>/gi, '\n')
                    .replace(/<\s*\/p\s*>/gi, '\n')
                    .replace(/<\s*p\s*>/gi, '')
                    .replace(/<\s*li\s*>/gi, '')
                    .replace(/<\s*\/li\s*>/gi, '\n')
                    .replace(/•/g, '\n• ')
                    .replace(/&nbsp;/gi, ' ')
                    .replace(/<[^>]+>/g, ''); // strip remaining tags

                  // Normalize whitespace
                  text = text.replace(/\r\n?|\n/g, '\n').replace(/\s+\n/g, '\n').replace(/\n\s+/g, '\n');

                  // Split by newlines first
                  let parts = text.split('\n')
                    .map((t) => t.replace(/^\s*[•\-–]\s*/, '').trim())
                    .filter(Boolean);

                  // If still a single long sentence, try split by numbered steps (e.g., 1. 2. 3.)
                  if (parts.length <= 1) {
                    const numbered = text
                      .split(/\s*(?=\d+\.)/g)
                      .map((t) => t.replace(/^\d+\.\s*/, '').trim())
                      .filter(Boolean);
                    if (numbered.length > 1) parts = numbered;
                  }

                  // As a final fallback, split by sentences if very long
                  if (parts.length <= 1) {
                    const sentences = text
                      .split(/(?<=[.!?])\s+(?=[A-Z0-9])/)
                      .map((t) => t.trim())
                      .filter(Boolean);
                    if (sentences.length > 1) parts = sentences;
                  }

                  return parts;
                };

                const steps = extractStepsFromDescription(product?.description);

                if (!steps || steps.length === 0) {
                  return null;
                }

                return (
                  <ul className="product-description-steps">
                    {steps.map((step, index) => (
                      <li key={index}>{step}</li>
                    ))}
                  </ul>
                );
              })()}
            </div>
            <div className="action-buttons">
               <button className="enquiry-button" onClick={handleEnquiryClick}>
                ENQUIRY NOW
              </button> 
              {/*<button className="whatsapp-button" onClick={handleWhatsAppClick}>
                <FaWhatsapp className="whatsapp-icon" />
                WhatsApp
              </button>*/}
            </div>
            
            <div className="social-share">
              <div className="share-title">Share On :</div>
              <div className="social-icons">
                <a href={`https://www.facebook.com/sharer/sharer.php?u=${shareUrl}`} target="_blank" rel="noopener noreferrer" className="social-icon"><FaFacebookF /></a>
                <a href={`https://www.instagram.com/`} target="_blank" rel="noopener noreferrer" className="social-icon"><FaInstagram /></a>
                <a href={`https://www.linkedin.com/sharing/share-offsite/?url=${shareUrl}`} target="_blank" rel="noopener noreferrer" className="social-icon"><FaLinkedinIn /></a>
                <a href={`https://twitter.com/intent/tweet?url=${shareUrl}&text=${shareTitle}`} target="_blank" rel="noopener noreferrer" className="social-icon"><FaTwitter /></a>
                <a href={`https://api.whatsapp.com/send?text=${shareTitle}%20-%20${shareUrl}`} target="_blank" rel="noopener noreferrer" className="social-icon"><FaWhatsapp /></a>
                <a href={`https://t.me/share/url?url=${shareUrl}&text=${shareTitle}`} target="_blank" rel="noopener noreferrer" className="social-icon"><FaTelegram /></a>
              </div>
            </div>
          </div>
        </div>
        
        {/* Product Finishes Section */}
        {hasFinishes() && (
          <div className="product-finishes-section">
            <h2 className="finishes-title">Available Finishes & Options</h2>
            
            {/* Tabs Navigation */}
            <div className="finishes-tabs">
              {product?.materials?.length > 0 && (
                <button 
                  className={`tab-button ${activeTab === 'materials' ? 'active' : ''}`}
                  onClick={() => handleTabClick('materials')}
                >
                  Materials
                </button>
              )}
              {product?.wooden?.length > 0 && (
                <button 
                  className={`tab-button ${activeTab === 'wooden' ? 'active' : ''}`}
                  onClick={() => handleTabClick('wooden')}
                >
                  Wooden Finishes
                </button>
              )}
              {product?.colors?.length > 0 && (
                <button 
                  className={`tab-button ${activeTab === 'colors' ? 'active' : ''}`}
                  onClick={() => handleTabClick('colors')}
                >
                  Colors
                </button>
              )}
              {product?.marbles?.length > 0 && (
                <button 
                  className={`tab-button ${activeTab === 'marble' ? 'active' : ''}`}
                  onClick={() => handleTabClick('marble')}
                >
                  Marble/Stone Finishes
                </button>
              )}
              {/* Add Size Tab */}
              {(product?.length || product?.width || product?.height || (product?.size_variants && product.size_variants.length > 0)) && (
                <button 
                  className={`tab-button ${activeTab === 'size' ? 'active' : ''}`}
                  onClick={() => handleTabClick('size')}
                >
                  Size Dimensions
                </button>
              )}
            </div>
            
            {/* Tab Contents */}
            <div className={`tab-content ${activeTab === 'materials' ? 'active' : ''}`}>
              <div className="finishes-grid">
                {product?.materials?.map((material, index) => (
                  <div className="finish-item" key={index}>
                    <div className="finish-image">
                      <img 
                        src={`${UPLOADS_BASE_URL}materials/${material.image}`}
                        alt={material.name}
                        onError={(e) => {
                          e.target.src = 'https://via.placeholder.com/150x150?text=No+Image';
                        }}
                      />
                    </div>
                    <span className="finish-name">{material.name}</span>
                  </div>
                ))}
              </div>
            </div>
            
            <div className={`tab-content ${activeTab === 'wooden' ? 'active' : ''}`}>
              <div className="finishes-grid">
                {product?.wooden?.map((wood, index) => (
                  <div className="finish-item" key={index}>
                    <div className="finish-image">
                      <img 
                        src={`${UPLOADS_BASE_URL}wooden/${wood.image}`}
                        alt={wood.name}
                        onError={(e) => {
                          e.target.src = 'https://via.placeholder.com/150x150?text=No+Image';
                        }}
                      />
                    </div>
                    <span className="finish-name">{wood.name}</span>
                  </div>
                ))}
              </div>
            </div>
            
            <div className={`tab-content ${activeTab === 'colors' ? 'active' : ''}`}>
              <div className="finishes-grid">
                {product?.colors?.map((color, index) => (
                  <div className="finish-item" key={index}>
                    <div className="finish-image">
                      <img 
                        src={`${UPLOADS_BASE_URL}colors/${color.image}`}
                        alt={color.name}
                        onError={(e) => {
                          e.target.src = 'https://via.placeholder.com/150x150?text=No+Image';
                        }}
                      />
                    </div>
                    <span className="finish-name">{color.name}</span>
                  </div>
                ))}
              </div>
            </div>
            
            <div className={`tab-content ${activeTab === 'marble' ? 'active' : ''}`}>
              <div className="finishes-grid">
                {product?.marbles?.map((marble, index) => (
                  <div className="finish-item" key={index}>
                    <div className="finish-image">
                      <img 
                        src={`${UPLOADS_BASE_URL}marbles/${marble.image}`}
                        alt={marble.name}
                        onError={(e) => {
                          e.target.src = 'https://via.placeholder.com/150x150?text=No+Image';
                        }}
                      />
                    </div>
                    <span className="finish-name">{marble.name}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Add Size Content Tab */}
            <div className={`tab-content ${activeTab === 'size' ? 'active' : ''}`}>
              <div className="size-dimensions-table">
                <table>
                  <thead>
                    <tr>
                      <th>Size Options</th>
                      <th>Dimensions (L x W x H)</th>
                    </tr>
                  </thead>
                  <tbody>
                    {/* Show size variants if available */}
                    {product?.size_variants && product.size_variants.length > 0 ? (
                      product.size_variants.map((variant, index) => (
                        <tr key={index}>
                          <td>{variant.name || `Size ${index + 1}`}</td>
                          <td>L-{parseFloat(variant.length)} CM x W-{parseFloat(variant.width)} CM x H-{parseFloat(variant.height)} CM</td>
                        </tr>
                      ))
                    ) : (
                      /* Fallback to single dimensions if no variants */
                      <>
                        {product?.length && (
                          <tr>
                            <td>Standard Size</td>
                            <td>L-{parseFloat(product.length)} CM x W-{parseFloat(product.width)} CM x H-{parseFloat(product.height)} CM</td>
                          </tr>
                        )}
                        {!product?.length && product?.width && (
                          <tr>
                            <td>Width</td>
                            <td>{parseFloat(product.width)} CM</td>
                          </tr>
                        )}
                        {!product?.length && !product?.width && product?.height && (
                          <tr>
                            <td>Height</td>
                            <td>{parseFloat(product.height)} CM</td>
                          </tr>
                        )}
                      </>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}
        
        {/* Product Details Tabs Section */}
        <div className="product-tabs-section">
          {/* Tabs Navigation */}
          <div className="detail-tabs">
            <button 
              className={`detail-tab-button ${activeDetailTab === 'description' ? 'active' : ''}`}
              onClick={() => handleDetailTabClick('description')}
            >
              Description
            </button>
            <button 
              className={`detail-tab-button ${activeDetailTab === 'vendor' ? 'active' : ''}`}
              onClick={() => handleDetailTabClick('vendor')}
            >
              Vendor Info
            </button>
            <button 
              className={`detail-tab-button ${activeDetailTab === 'shipping' ? 'active' : ''}`}
              onClick={() => handleDetailTabClick('shipping')}
            >
              Delivery Information
            </button>
          </div>
          
          {/* Description Tab Content */}
          <div className={`detail-tab-content ${activeDetailTab === 'description' ? 'active' : ''}`}>
            {/* <div dangerouslySetInnerHTML={{ __html: product.description }} /> */}
            
            <ul className="specs-list">
              {product.category_name && (
                <li>
                  <span className="label">Category:</span> 
                  {product.category_name}
                  {product.subcategory_name && (
                    <> &gt; {product.subcategory_name}</>
                  )}
                </li>
              )}
              {product?.materials?.length > 0 && (
                <li>
                  <span className="label">Materials:</span> 
                  {product.materials.map(material => material.name).join(', ')}
                </li>
              )}
              {product?.colors?.length > 0 && (
                <li>
                  <span className="label">Available Colors:</span> 
                  {product.colors.map(color => color.name).join(', ')}
                </li>
              )}
              {product?.wooden?.length > 0 && (
                <li>
                  <span className="label">Wooden Finishes:</span> 
                  {product.wooden.map(wood => wood.name).join(', ')}
                </li>
              )}
              {product?.marbles?.length > 0 && (
                <li>
                  <span className="label">Marble/Stone Finishes:</span> 
                  {product.marbles.map(marble => marble.name).join(', ')}
                </li>
              )}
            </ul>
          </div>
          
          {/* Vendor Info Tab Content */}
          <div className={`detail-tab-content ${activeDetailTab === 'vendor' ? 'active' : ''}`}>
            <div className="vendor-info">
              <h3>Design Craft</h3>
              <div className="vendor-address">
                <p>Abdul Razzaq Mohiddin Abdulla</p>
                <p>Warehouse Shed No-1 Opp. Pedal Art</p>
                <p>Al Quoz Industrial Area – 1, Dubai</p>
              </div>
              <p>
                At Design Craft, we blend artistry with functionality to create office furniture that transforms workspaces. Our premium materials and skilled craftsmanship ensure every piece meets the highest standards of quality and aesthetics.
              </p>
            </div>
          </div>
          
          {/* Shipping Tab Content */}
          <div className={`detail-tab-content ${activeDetailTab === 'shipping' ? 'active' : ''}`}>
            <div className="shipping-info">
              <h3>Delivery Information</h3>
              <ul>
                <li>Free delivery across all Emirates on orders above AED 5,000</li>
                <li>Delivery charges apply for orders below AED 5,000</li>
                <li>Standard project timeframe: 10–15 business days</li>
                <li>Express delivery options may be available upon request (subject to availability)</li>
                <li>Professional installation provided by our in-house team</li>
              </ul>
              
              </div>
          </div>
        </div>
        
        {/* Related Products Section */}
        {relatedProducts.length > 0 && (
          <div className="related-products-section">
            <h2 className="related-title">Related Products</h2>
            <p className="related-subtitle">
              Explore more {product.subcategory_name} options that match your style.
            </p>
            
            <div className="related-products-slider">
              <button 
                className="slider-nav-button slider-nav-prev" 
                onClick={() => handleSlideChange('prev')}
                aria-label="Previous products"
              >
                <FiChevronLeft />
              </button>
              
              <div className="related-products-grid" ref={sliderRef}>
                {relatedProducts.map(relatedProduct => (
                  <div key={relatedProduct.id} className="related-product-item">
                    <Link to={`/product/${relatedProduct.id}`}>
                      <div className="related-product-image">
                        <img 
                          src={`${UPLOADS_BASE_URL}products/${relatedProduct.images[0]}`}
                          alt={relatedProduct.name}
                          onError={(e) => {
                            e.target.src = 'https://via.placeholder.com/300x300?text=Image+Not+Found';
                          }}
                        />
                      </div>
                      <div className="related-product-info">
                        <h3 className="related-product-title">{relatedProduct.name}</h3>
                      </div>
                    </Link>
                  </div>
                ))}
              </div>
              
              <button 
                className="slider-nav-button slider-nav-next" 
                onClick={() => handleSlideChange('next')}
                aria-label="Next products"
              >
                <FiChevronRight />
              </button>
            </div>
          </div>
        )}
      </div>

      <EnquiryForm
        isOpen={isEnquiryOpen}
        onClose={handleEnquiryClose}
        productName={product.name}
        productSku={product.sku}
      />

      <style jsx>{`
        .custom-breadcrumbs {
          background-color: #ffffff;
          padding: 15px 0;
          border-bottom: 1px solid #e5e5e5;
          margin-top: 80px;
          box-shadow: 0 2px 4px rgba(0,0,0,0.02);
        }

        .breadcrumbs-container {
          max-width: 1200px;
          margin: 0 auto;
          padding: 0 20px;
        }

        .breadcrumbs-content {
          display: flex;
          align-items: center;
          min-height: 40px;
        }

        .breadcrumbs-links {
          display: flex;
          align-items: center;
          flex-wrap: wrap;
          gap: 5px;
          font-size: 13px;
          font-weight: 400;
        }

        .breadcrumbs-links a {
          color: #666;
          text-decoration: none;
          transition: color 0.2s ease;
          padding: 5px 0;
        }

        .breadcrumbs-links a:hover {
          color: #ff7f50;
        }

        .separator {
          color: #ccc;
          margin: 0 5px;
          font-size: 12px;
        }

        .current {
          color: #333;
          font-weight: 500;
        }

        @media (max-width: 768px) {
          .custom-breadcrumbs {
            margin-top: 60px;
            padding: 12px 0;
          }

          .breadcrumbs-content {
            min-height: 35px;
          }

          .breadcrumbs-links {
            font-size: 12px;
          }
        }

        @media (max-width: 480px) {
          .custom-breadcrumbs {
            margin-top: 50px;
            padding: 10px 0;
          }

          .breadcrumbs-links {
            font-size: 11px;
          }

          .separator {
            margin: 0 3px;
          }
        }
      `}</style>
    </div>
  );
};

export default ProductDetailPage; 

<style jsx>{`
  .specs-list {
    list-style: none;
    padding: 0;
    margin: 20px 0;
  }

  .specs-list li {
    margin-bottom: 12px;
    font-size: 15px;
    color: #333;
  }

  .specs-list .label {
    font-weight: 600;
    color: #666;
    min-width: 150px;
    display: inline-block;
  }

  .product-category,
  .subcategory {
    display: block;
    margin-bottom: 10px;
    font-size: 15px;
    color: #666;
  }

  .product-category-info {
    margin-bottom: 20px;
    padding: 15px 0;
    border-bottom: 1px solid #f0f0f0;
  }

  .product-category-info .product-category,
  .product-category-info .subcategory {
    display: block;
    margin-bottom: 8px;
    font-size: 15px;
    color: #666;
    line-height: 1.4;
  }

  .subcategory-icon {
    margin-right: 8px;
    color: #999;
    font-size: 12px;
  }

  .label {
    display: inline-block;
    min-width: 100px;
    margin-right: 10px;
  }

  strong {
    color: #333;
    font-weight: 600;
  }

  .product-category a {
    color: #333;
    text-decoration: none;
    font-weight: 600;
  }

  .product-category a:hover {
    color: #007bff;
  }

  .product-description {
    margin-top: 20px;
  }

  .action-buttons {
    display: flex !important;
    gap: 15px;
    margin: 20px 0;
    flex-wrap: wrap;
    align-items: center;
  }

  .whatsapp-button {
    background: #25D366 !important;
    color: white !important;
    border: none !important;
    padding: 15px 30px !important;
    border-radius: 8px !important;
    font-size: 16px !important;
    font-weight: 600 !important;
    cursor: pointer !important;
    transition: all 0.3s ease !important;
    display: flex !important;
    align-items: center !important;
    gap: 8px !important;
    min-width: 180px !important;
    justify-content: center !important;
    box-shadow: 0 2px 10px rgba(37, 211, 102, 0.3) !important;
    text-decoration: none !important;
    outline: none !important;
    position: relative !important;
    overflow: hidden !important;
  }

  .whatsapp-button:hover {
    background: #128C7E !important;
    transform: translateY(-2px) !important;
    box-shadow: 0 4px 20px rgba(37, 211, 102, 0.5) !important;
  }

  .whatsapp-button:active {
    transform: translateY(0px) !important;
    box-shadow: 0 2px 10px rgba(37, 211, 102, 0.4) !important;
  }

  .whatsapp-button:focus {
    outline: 2px solid #25D366 !important;
    outline-offset: 2px !important;
  }

  .whatsapp-icon {
    font-size: 18px !important;
    margin-right: 5px !important;
  }

  @media (max-width: 768px) {
    .action-buttons {
      flex-direction: column;
      gap: 10px;
    }
    
    .whatsapp-button {
      min-width: 100%;
      justify-content: center;
    }
  }
`}</style> 